import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToEl, scrollToTop } from "@/lib/scroll";
import ThemeToggle from "./ThemeToggle";

const links = [
  { label: "Programs", href: "#services" },
  { label: "Schedule", href: "#schedule" },
  { label: "Pricing", href: "#pricing" },
  { label: "Clinics", href: "/clinics/power-the-paddle", external: true },
  { label: "Results", href: "#results" },
  { label: "Coach", href: "#about" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToEl(href);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-black/60 border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 h-20 flex items-center justify-between">
        <button
          onClick={scrollToTop}
          className="font-display text-lg md:text-xl tracking-tight text-white leading-none text-left"
          data-testid="logo-btn"
        >
          GETFIT<span className="text-[#005EEA]">/</span>JEANCLAUDE
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            l.external ? (
              <Link
                key={l.href}
                to={l.href}
                className="font-mono-accent text-xs text-[#005EEA] hover:text-white transition-colors"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="font-mono-accent text-xs text-[#A0A0A0] hover:text-white transition-colors"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </button>
            )
          ))}
          <ThemeToggle />
          <button
            onClick={() => go("#consult")}
            className="font-mono-accent text-xs bg-[#005EEA] text-white px-5 py-3 hover:bg-[#0047b3] transition-colors"
            data-testid="nav-consult-btn"
          >
            Free Consult
          </button>
        </nav>

        <div className="lg:hidden flex items-center gap-3">
          <ThemeToggle />
          <button
            className="text-white"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 border-t border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {links.map((l) => (
                l.external ? (
                  <Link
                    key={l.href}
                    to={l.href}
                    onClick={() => setOpen(false)}
                    className="font-display text-2xl text-[#005EEA] text-left"
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                ) : (
                  <button
                    key={l.href}
                    onClick={() => go(l.href)}
                    className="font-display text-2xl text-white text-left"
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </button>
                )
              ))}
              <button
                onClick={() => go("#consult")}
                className="font-mono-accent text-xs bg-[#005EEA] text-white px-5 py-4 mt-2"
                data-testid="mobile-nav-consult-btn"
              >
                Book Free Consult
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
